import React from "react"
import { ArrowLeft, Eye, Calendar, Clock, ExternalLink, ShieldCheck, Sparkles, Languages, TrendingUp, Radio, Film, BadgeCheck, Heart, MessageCircle, Share2, Percent, Music, Hash, AtSign, Bookmark, Users, Search } from "lucide-react"
import { CONTENT_TYPE_META, SENTIMENT_META } from "../constants"
import { formatViews, formatUtcDate } from "../utils"
import Thumbnail from "./Thumbnail"
import ReelPlayer from "./ReelPlayer"
import TikTokPlayer from "./TikTokPlayer"
import Avatar from "./Avatar"
import CaptionText from "./CaptionText"
import { useAnalytics, ANALYTICS_EVENTS } from "../../../analytics"

const AUDIO_TYPE_LABEL = {
    original_sounds: "Original audio",
    licensed_music: "Licensed music",
}

const Chip = ({ label, color }) => (
    <span style={{ background: `${color}18`, color, fontSize: "12px", fontWeight: 600, padding: "4px 10px", borderRadius: "6px" }}>
        {label}
    </span>
)

const SidebarCard = ({ title, children }) => (
    <div style={{ background: "#1c1d27", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "14px", padding: "18px" }}>
        <h6 style={{ color: "#fff", fontWeight: 600, fontSize: "12.5px", textTransform: "uppercase", letterSpacing: "0.04em", opacity: 0.7, marginBottom: "12px" }}>
            {title}
        </h6>
        {children}
    </div>
)

/**
 * Full-page detail view for one verified video - replaces the old modal so all
 * the info (summary, verification reasoning, transcript basis, view history)
 * has room to breathe instead of being crammed into a popup. Rendered inline in
 * place of the video grid; `onBack` returns to it, same "swap the panel"
 * pattern used for search -> selected-app elsewhere in the page.
 */
const VideoDetailSection = ({ video, onBack }) => {
    const { trackEvent } = useAnalytics()
    if (!video) return null

    const isInstagram = video.platform === "instagram"
    const isTikTok = video.platform === "tiktok"
    // Both platforms' source video is vertical (9:16) - see the matching
    // portrait treatment below.
    const isPortrait = isInstagram || isTikTok
    const contentType = CONTENT_TYPE_META[video.summary?.content_type] || CONTENT_TYPE_META.unknown
    const sentiment = SENTIMENT_META[video.summary?.sentiment] || SENTIMENT_META.unknown
    const isMetaBased = video.isMetaBased
    const verification = video.verification || {}
    const transcript = video.transcript || {}
    const statsHistory = Array.isArray(video.stats_history) ? video.stats_history : []
    const hashtags = Array.isArray(video.hashtags) ? video.hashtags : []
    const mentions = Array.isArray(video.mentions) ? video.mentions : []

    return (
        <div>
            <button onClick={onBack} className="ugc-btn-ghost mb-4">
                <ArrowLeft size={15} /> Back to {isInstagram ? "reels" : "videos"}
            </button>
            {/* ("reels" only for Instagram - TikTok and YouTube both say "videos".) */}

            <div className="ugc-detail-grid">
                {/* ── Main column ─────────────────────────────────────── */}
                <div>
                    {/* Reels and TikTok videos are both vertical (9:16)
                        source video - capped to a modestly-sized portrait
                        column and centered instead of stretching to a
                        YouTube-width 16:9 frame (which made a 9:16 source
                        enormous). Instagram gets the real, playable file
                        (ReelPlayer) - TikTok's payload carries no confirmed
                        playable file field (only the tiktok.com permalink, a
                        page rather than a stream per TIKTOK_VIDEO_API.md §5),
                        so it renders the mirrored `thumb_url` still frame with
                        the same permanent "Watch on TikTok" overlay the grid
                        card uses (TikTokPlayer), rather than a bare thumbnail.

                        This uses `aspect-ratio` rather than the usual
                        percentage `padding-top` trick: a percentage
                        padding-top/bottom is resolved against the
                        *containing block's* width, not this element's own
                        (max-width-capped) width - so combined with
                        `maxWidth: 300px` it was computing height off the
                        full ~2fr column width instead of the capped 300px,
                        which is what actually produced the oversized box. */}
                    <div style={{
                        position: "relative", width: "100%",
                        maxWidth: isPortrait ? "300px" : "100%",
                        margin: isPortrait ? "0 auto" : 0,
                        aspectRatio: isPortrait ? "9 / 16" : "16 / 9",
                        borderRadius: "16px", overflow: "hidden", background: "#000",
                    }}>
                        {isInstagram ? (
                            <ReelPlayer
                                video={video}
                                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                            />
                        ) : isTikTok ? (
                            <TikTokPlayer
                                video={video}
                                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                            />
                        ) : (
                            <Thumbnail
                                src={video.thumbnail_url}
                                alt={video.title}
                                isInstagram={isInstagram}
                                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                            />
                        )}
                        {/* pointerEvents: none for Instagram/TikTok so these
                            purely informational badges never intercept clicks
                            meant for the real <video controls> bar (Instagram)
                            or the "Watch on TikTok" overlay pill underneath. */}
                        <div style={{ position: "absolute", top: "12px", left: "12px", display: "flex", gap: "6px", pointerEvents: isPortrait ? "none" : "auto" }}>
                            {video.is_live && (
                                <span style={{ display: "flex", alignItems: "center", gap: "4px", background: "#ef4444", color: "#fff", fontSize: "11px", fontWeight: 700, padding: "3px 8px", borderRadius: "6px" }}>
                                    <Radio size={11} /> LIVE
                                </span>
                            )}
                            {video.is_short && (
                                <span style={{ display: "flex", alignItems: "center", gap: "4px", background: "#8b5cf6", color: "#fff", fontSize: "11px", fontWeight: 700, padding: "3px 8px", borderRadius: "6px" }}>
                                    <Film size={11} /> SHORTS
                                </span>
                            )}
                            {video.is_reel && (
                                <span style={{ display: "flex", alignItems: "center", gap: "4px", background: "#E1306C", color: "#fff", fontSize: "11px", fontWeight: 700, padding: "3px 8px", borderRadius: "6px" }}>
                                    <Film size={11} /> REEL
                                </span>
                            )}
                            {video.is_tiktok && (
                                <span style={{ display: "flex", alignItems: "center", gap: "4px", background: "#000", color: "#25F4EE", fontSize: "11px", fontWeight: 700, padding: "3px 8px", borderRadius: "6px" }}>
                                    <Film size={11} /> TIKTOK
                                </span>
                            )}
                        </div>
                        {video.duration_text && (
                            <span style={{ position: "absolute", bottom: "12px", right: "12px", background: "rgba(0,0,0,0.8)", color: "#fff", fontSize: "12px", fontWeight: 600, padding: "3px 8px", borderRadius: "6px", pointerEvents: isPortrait ? "none" : "auto" }}>
                                {video.duration_text}
                            </span>
                        )}
                    </div>

                    <h1 className="text-white mt-4 mb-2" style={{ fontWeight: 700, fontSize: "22px", lineHeight: "1.4" }}>
                        {isPortrait ? <CaptionText text={video.title} /> : video.title}
                    </h1>

                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
                        <div className="d-flex align-items-center gap-3 flex-wrap" style={{ fontSize: "13px", color: "#9ca3af" }}>
                            <a href={video.channel_url} target="_blank" rel="noopener noreferrer" style={{ color: "#fff", fontWeight: 600, textDecoration: "none", display: "flex", alignItems: "center", gap: "6px" }}>
                                <Avatar src={video.profile_pic_url} alt={video.channel_name} size={20} />
                                {video.channel_name}
                                {/* Account verification is Instagram-only - TikTok's
                                    source reports no verified badge at all
                                    (TIKTOK_VIDEO_API.md §1 "not reported - use
                                    `min_followers`"), so this never renders for it. */}
                                {video.account_verified && <BadgeCheck size={14} color="#3b82f6" title="Verified Instagram account" />}
                            </a>
                            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Eye size={13} /> {formatViews(video.metrics?.views)} {isInstagram ? "plays" : "views"}</span>
                            {isTikTok && video.author_followers != null && (
                                <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Users size={13} /> {formatViews(video.author_followers)} followers</span>
                            )}
                            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Calendar size={13} /> {video.published_time_text}</span>
                            {video.duration_text && <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Clock size={13} /> {video.duration_text}</span>}
                        </div>
                        <a
                            href={video.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ugc-btn-primary"
                            style={{ textDecoration: "none", width: "auto" }}
                            onClick={() => trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_OUTBOUND_CLICKED, {
                                video_id: video.video_id,
                                platform: video.platform,
                                link_type: "watch",
                            })}
                        >
                            Watch on {isInstagram ? "Instagram" : isTikTok ? "TikTok" : "YouTube"} <ExternalLink size={14} />
                        </a>
                    </div>

                    <div className="d-flex gap-2 flex-wrap mb-4">
                        <Chip label={contentType.label} color={contentType.color} />
                        <Chip label={sentiment.label} color={sentiment.color} />
                        {verification.verified && (
                            <span style={{ display: "flex", alignItems: "center", gap: "5px", background: "rgba(16,185,129,0.12)", color: "#10b981", fontSize: "12px", fontWeight: 600, padding: "4px 10px", borderRadius: "6px" }}>
                                <ShieldCheck size={13} /> Verified {Math.round((verification.confidence || 0) * 100)}% confidence
                            </span>
                        )}
                        {isMetaBased && (
                            <span style={{ display: "flex", alignItems: "center", gap: "5px", color: "#6b7280", fontSize: "12px" }}>
                                <Sparkles size={13} />
                                {isInstagram
                                    ? "No caption was written for this reel - summarized from its hashtags, account & audio"
                                    : isTikTok
                                        ? "No caption was written for this video - summarized from its hashtags, account & sound"
                                        : "Summarized from title & description (no transcript available)"}
                            </span>
                        )}
                        {isPortrait && !isMetaBased && (
                            <span style={{ display: "flex", alignItems: "center", gap: "5px", color: "#6b7280", fontSize: "12px" }}>
                                <Sparkles size={13} /> Summarized from this {isInstagram ? "reel's" : "video's"} caption (the video itself was never watched)
                            </span>
                        )}
                    </div>

                    {(video.metrics?.likes != null || video.metrics?.comments != null || video.metrics?.shares != null || video.metrics?.saves != null || video.signals?.engagement_rate != null) && (
                        <div className="d-flex gap-3 flex-wrap mb-4" style={{ fontSize: "13px", color: "#d1d5db" }}>
                            {video.metrics?.likes != null && (
                                <span style={{ display: "flex", alignItems: "center", gap: "5px" }}><Heart size={14} /> {formatViews(video.metrics.likes)} likes</span>
                            )}
                            {video.metrics?.comments != null && (
                                <span style={{ display: "flex", alignItems: "center", gap: "5px" }}><MessageCircle size={14} /> {formatViews(video.metrics.comments)} comments</span>
                            )}
                            {video.metrics?.shares != null && (
                                <span style={{ display: "flex", alignItems: "center", gap: "5px" }}><Share2 size={14} /> {formatViews(video.metrics.shares)} shares</span>
                            )}
                            {video.metrics?.saves != null && (
                                <span title="Saves - the strongest intent signal TikTok reports" style={{ display: "flex", alignItems: "center", gap: "5px" }}><Bookmark size={14} /> {formatViews(video.metrics.saves)} saves</span>
                            )}
                            {video.signals?.engagement_rate != null && (
                                <span title={isTikTok ? "(likes + comments + shares + saves) / views" : `(likes + comments + shares) / ${isInstagram ? "plays" : "views"}`} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                    <Percent size={14} /> {(video.signals.engagement_rate * 100).toFixed(1)}% engagement
                                </span>
                            )}
                        </div>
                    )}

                    {video.summary?.summary && (
                        <div className="mb-4">
                            <h6 style={{ color: "#fff", fontWeight: 600, fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.04em", opacity: 0.7, marginBottom: "10px" }}>
                                What this {isInstagram ? "reel" : "video"} says
                            </h6>
                            <p style={{ color: "#d1d5db", fontSize: "14.5px", lineHeight: "1.7", margin: 0 }}>{video.summary.summary}</p>
                        </div>
                    )}

                    {Array.isArray(video.summary?.key_points) && video.summary.key_points.length > 0 && (
                        <div className="mb-4">
                            <h6 style={{ color: "#fff", fontWeight: 600, fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.04em", opacity: 0.7, marginBottom: "10px" }}>
                                Key points
                            </h6>
                            <ul style={{ color: "#d1d5db", fontSize: "14px", lineHeight: "1.9", paddingLeft: "20px", margin: 0 }}>
                                {video.summary.key_points.map((point, i) => <li key={i}>{point}</li>)}
                            </ul>
                        </div>
                    )}

                    {(verification.evidence || verification.reason) && (
                        <div className="mb-2">
                            <h6 style={{ color: "#fff", fontWeight: 600, fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.04em", opacity: 0.7, marginBottom: "10px" }}>
                                Why it's here
                            </h6>
                            {verification.evidence && (
                                <p style={{ color: "#adb5bd", fontSize: "13.5px", lineHeight: "1.7", margin: "0 0 8px" }}>
                                    <strong style={{ color: "#fff" }}>Evidence: </strong>{verification.evidence}
                                </p>
                            )}
                            {verification.reason && (
                                <p style={{ color: "#adb5bd", fontSize: "13.5px", lineHeight: "1.7", margin: 0 }}>
                                    <strong style={{ color: "#fff" }}>Reasoning: </strong>{verification.reason}
                                </p>
                            )}
                            {verification.escalated && (
                                <p style={{ color: "#6b7280", fontSize: "12px", marginTop: "8px", marginBottom: 0 }}>
                                    Re-checked against the transcript after an uncertain first pass
                                    {verification.first_pass_confidence != null && ` (initial confidence ${Math.round(verification.first_pass_confidence * 100)}%)`}.
                                </p>
                            )}
                            {Array.isArray(video.relevance?.matched_terms) && video.relevance.matched_terms.length > 0 && (
                                <p style={{ color: "#6b7280", fontSize: "12px", marginTop: "8px", marginBottom: 0 }}>
                                    <strong style={{ color: "#9ca3af" }}>Matched on: </strong>
                                    {video.relevance.matched_terms.join(", ")}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Sidebar column ──────────────────────────────────── */}
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {isInstagram && (
                        <SidebarCard title="Caption & audio">
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#d1d5db", fontSize: "13px" }}>
                                <Languages size={14} />
                                {video.analysis_basis === "caption" ? "Judged from the reel's caption" : "No caption written - judged from tags, account & audio"}
                            </div>
                            {video.audio?.type && (
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#d1d5db", fontSize: "13px", marginTop: "10px" }}>
                                    <Music size={14} />
                                    {AUDIO_TYPE_LABEL[video.audio.type] || video.audio.type}
                                    {video.audio.title && ` - "${video.audio.title}"`}
                                    {video.audio.artist && ` by ${video.audio.artist}`}
                                </div>
                            )}
                            {hashtags.length > 0 && (
                                <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", color: "#8b9bd1", fontSize: "12.5px", marginTop: "10px" }}>
                                    <Hash size={14} style={{ flexShrink: 0, marginTop: "2px" }} />
                                    <span>{hashtags.map(t => `#${t}`).join(" ")}</span>
                                </div>
                            )}
                            {mentions.length > 0 && (
                                <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", color: "#9ca3af", fontSize: "12.5px", marginTop: "10px" }}>
                                    <AtSign size={14} style={{ flexShrink: 0, marginTop: "2px" }} />
                                    <span>{mentions.join(", ")}</span>
                                </div>
                            )}
                        </SidebarCard>
                    )}

                    {/* TikTok's payload carries only a sound title/author -
                        no original-vs-licensed flag like Instagram's `audio.type`
                        (TIKTOK_VIDEO_API.md §1 "title only - there is no
                        original-vs-licensed flag"). */}
                    {isTikTok && (
                        <SidebarCard title="Caption & music">
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#d1d5db", fontSize: "13px" }}>
                                <Languages size={14} />
                                {video.analysis_basis === "caption" ? "Judged from the video's caption" : "No caption written - judged from tags, account & sound"}
                            </div>
                            {video.music?.title && (
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#d1d5db", fontSize: "13px", marginTop: "10px" }}>
                                    <Music size={14} />
                                    "{video.music.title}"{video.music.author && ` by ${video.music.author}`}
                                </div>
                            )}
                            {hashtags.length > 0 && (
                                <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", color: "#8b9bd1", fontSize: "12.5px", marginTop: "10px" }}>
                                    <Hash size={14} style={{ flexShrink: 0, marginTop: "2px" }} />
                                    <span>{hashtags.map(t => `#${t}`).join(" ")}</span>
                                </div>
                            )}
                            {mentions.length > 0 && (
                                <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", color: "#9ca3af", fontSize: "12.5px", marginTop: "10px" }}>
                                    <AtSign size={14} style={{ flexShrink: 0, marginTop: "2px" }} />
                                    <span>{mentions.join(", ")}</span>
                                </div>
                            )}
                            {video.matched_keyword && (
                                <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", color: "#6b7280", fontSize: "12.5px", marginTop: "10px" }}>
                                    <Search size={14} style={{ flexShrink: 0, marginTop: "2px" }} />
                                    <span>Discovered via "{video.matched_keyword}"</span>
                                </div>
                            )}
                        </SidebarCard>
                    )}

                    {!isPortrait && (
                        <SidebarCard title="Transcript basis">
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#d1d5db", fontSize: "13px" }}>
                                <Languages size={14} />
                                {transcript.available
                                    ? `Available${transcript.language ? ` (${transcript.language})` : ""}${transcript.is_generated ? ", auto-generated" : ""}`
                                    : "Not available for this video"}
                            </div>
                            {transcript.suspect && (
                                <p style={{ color: "#f59e0b", fontSize: "12px", marginTop: "8px", marginBottom: 0, lineHeight: "1.5" }}>
                                    The captions didn't match this video's description, so they weren't used for the summary.
                                </p>
                            )}
                        </SidebarCard>
                    )}

                    {statsHistory.length > 0 && (
                        <SidebarCard title="View history">
                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                {statsHistory.map((entry, i) => (
                                    <div key={i} style={{
                                        display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "12.5px",
                                        color: "#9ca3af", padding: "8px 0",
                                        borderBottom: i < statsHistory.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                                    }}>
                                        <span>{formatUtcDate(entry.at) || "—"}</span>
                                        <span style={{ display: "flex", alignItems: "center", gap: "5px", color: "#d1d5db" }}>
                                            <TrendingUp size={12} /> {formatViews(entry.views)}
                                            {entry.likes != null && <span style={{ display: "flex", alignItems: "center", gap: "3px" }}><Heart size={11} /> {formatViews(entry.likes)}</span>}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </SidebarCard>
                    )}

                    <SidebarCard title="Video info">
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "13px", color: "#d1d5db" }}>
                            <div className="d-flex justify-content-between"><span style={{ color: "#6b7280" }}>{isInstagram ? "Plays" : "Views"}</span><span>{formatViews(video.metrics?.views)}</span></div>
                            {video.metrics?.likes != null && (
                                <div className="d-flex justify-content-between"><span style={{ color: "#6b7280" }}>Likes</span><span>{formatViews(video.metrics.likes)}</span></div>
                            )}
                            {video.metrics?.comments != null && (
                                <div className="d-flex justify-content-between"><span style={{ color: "#6b7280" }}>Comments</span><span>{formatViews(video.metrics.comments)}</span></div>
                            )}
                            {video.metrics?.shares != null && (
                                <div className="d-flex justify-content-between"><span style={{ color: "#6b7280" }}>Shares</span><span>{formatViews(video.metrics.shares)}</span></div>
                            )}
                            {video.metrics?.saves != null && (
                                <div className="d-flex justify-content-between"><span style={{ color: "#6b7280" }}>Saves</span><span>{formatViews(video.metrics.saves)}</span></div>
                            )}
                            {video.signals?.engagement_rate != null && (
                                <div className="d-flex justify-content-between"><span style={{ color: "#6b7280" }}>Engagement rate</span><span>{(video.signals.engagement_rate * 100).toFixed(1)}%</span></div>
                            )}
                            {isTikTok && video.author_followers != null && (
                                <div className="d-flex justify-content-between"><span style={{ color: "#6b7280" }}>Creator followers</span><span>{formatViews(video.author_followers)}</span></div>
                            )}
                            {isTikTok && video.signals?.creator?.views_per_follower != null && (
                                <div className="d-flex justify-content-between" title="Views this video got, relative to the creator's own follower count."><span style={{ color: "#6b7280" }}>Views per follower</span><span>{video.signals.creator.views_per_follower.toFixed(1)}x</span></div>
                            )}
                            <div className="d-flex justify-content-between"><span style={{ color: "#6b7280" }}>Published</span><span>{video.published_time_text}</span></div>
                            <div className="d-flex justify-content-between"><span style={{ color: "#6b7280" }}>Duration</span><span>{video.duration_text || "—"}</span></div>
                        </div>
                    </SidebarCard>
                </div>
            </div>

            <style>{`
                .ugc-detail-grid {
                    display: grid;
                    grid-template-columns: minmax(0, 2fr) minmax(240px, 1fr);
                    gap: 28px;
                }
                @media (max-width: 900px) {
                    .ugc-detail-grid { grid-template-columns: 1fr; }
                }
            `}</style>
        </div>
    )
}

export default VideoDetailSection
